import React, { useState } from 'react';
import { Tab, TabList, TabPanel, Tabs } from '../../../../CommonComponents/OPL/Tab';
import { Wrapper, WrapperProps } from '../../../../CommonComponents/OPL/Wrapper/Wrapper';
import { useGoal } from '../../hooks/useGoal';
import { NavigationData, useNavigation } from '../../hooks/useNavigation';
import { GOAL_TAB_MAP } from './Goal.constants';
import { GoalLoading } from './Goal.loading';
import { useRouteParams } from '../../hooks/useRouteParams';
import { GoalTypeDTO } from '../../../types/goalsDTO';
import { EstatePlanningTab } from '../../goals/EstatePlanning/EstatePlanning.types';
import { Lozenge } from '../../../../CommonComponents/OPL/Lozenge/Lozenge';
import VisuallyHidden from '@reach/visually-hidden';

interface Props extends WrapperProps {}

export const Goal = (props: Props) => {
  const { isLoading, data } = useGoal();

  const { data: navigationData, isLoading: navigationDataIsLoading } = useNavigation();

  const { tab: initialTab } = useRouteParams();

  if (isLoading || navigationDataIsLoading) {
    return <GoalLoading />;
  }

  if (!data) {
    return null;
  }

  const { GoalType } = data;

  if (!GoalType) {
    return null;
  }

  const initialTabIndex = navigationData
    ? navigationData.tabs.findIndex(tab => tab.split(' ').join('') === initialTab)
    : 0;

  return (
    <GoalContent
      initialTabIndex={initialTabIndex >= 0 ? initialTabIndex : 0}
      goalType={GoalType}
      navigationData={navigationData}
      {...props}
    />
  );
};

interface GoalContentProps extends WrapperProps {
  initialTabIndex: number;
  goalType: GoalTypeDTO;
  navigationData: NavigationData | null;
}

const GoalContent = (props: GoalContentProps) => {
  const { goalType, initialTabIndex, navigationData, ...rest } = props;

  const [selectedTabIndex, setSelectedTabIndex] = useState(initialTabIndex);

  const { clientId, goalId } = useRouteParams();

  const tabMap = GOAL_TAB_MAP[goalType];

  if (Object.keys(tabMap).length === 1) {
    const Component = tabMap[Object.keys(tabMap)[0]];

    return (
      <Wrapper {...rest}>
        <Component />
      </Wrapper>
    );
  }

  if (!navigationData) {
    return null;
  }

  const { tabs } = navigationData;

  const hasEstateFlow = tabs.includes('Estate Flow');

  return (
    <Wrapper {...rest}>
      <VisuallyHidden as="h1">Goal Page</VisuallyHidden>
      <Tabs
        selectedIndex={selectedTabIndex}
        onSelect={index => {
          if (hasEstateFlow) {
            ADV.util.Router.go2('Goal', {
              clientId,
              goalId,
              tab: tabs[index] === 'Estate Flow' ? 'EstateFlow' : '',
            });
          }
          setSelectedTabIndex(index);
        }}
      >
        <TabList>
          {tabs.map(tab => (
            <Tab key={tab}>
              {tab}
              {tab === EstatePlanningTab.EstateFlow && (
                <Lozenge variantColor="new" size="small" ml="4" style={{ transform: 'scale(0.8)' }}>
                  BETA
                </Lozenge>
              )}
            </Tab>
          ))}
        </TabList>

        {tabs.map(tab => {
          const Component = tabMap[tab];

          return (
            <TabPanel key={tab}>
              <Component />
            </TabPanel>
          );
        })}
      </Tabs>
    </Wrapper>
  );
};
